import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Stack,
  Divider
} from "@mui/material";
import { useState } from "react";
import ReceiveFormHeader from "../components/StockReceiving/ReceiveFormHeader";
import ProductVariantPicker from "../components/StockReceiving/ProductVariantPicker";
import ReceivingTable from "../components/StockReceiving/ReceivingTable";
import CostSummary from "../components/StockReceiving/CostSummary";
import { createStockReceipt } from "../api/adminStockApi";
import { createSupplier } from "../api/adminSupplierApi";
import InventoryIcon from "@mui/icons-material/Inventory";

type StockReceiptItem = {
  variant_id: number;
  stock_type: "in_store" | "online";
  quantity: number;
  unit_cost: number;
  sku?: string;
  variant_name?: string;
  product_name?: string;
  image_url?: string;
};

type FormData = {
  supplier_id: number | null;
  new_supplier_name: string;
  invoice_number: string;
  order_number: string;
  delivery_date: string;
  invoice_date: string;
  note: string;
};

const StockReceivePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<FormData>({
    supplier_id: null,
    new_supplier_name: "",
    invoice_number: "",
    order_number: "",
    delivery_date: new Date().toISOString().split("T")[0],
    invoice_date: "",
    note: "",
  });

  const [items, setItems] = useState<StockReceiptItem[]>([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [importDuty, setImportDuty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if ((!formData.supplier_id && !formData.new_supplier_name) || !formData.order_number || items.length === 0) {
      setError("Please fill in required fields and add at least one item.");
      return;
    }

    setError("");
    setLoading(true);
    setSuccess(false);

    try {
      let supplierId = formData.supplier_id;
if (!supplierId && formData.new_supplier_name.trim()) {
  const created = await createSupplier({ name: formData.new_supplier_name.trim() });
  supplierId = created.id;
  // ✅ Update form state too
  setFormData((prev) => ({ ...prev, supplier_id: supplierId }));
}


      await createStockReceipt({
        supplier_id: supplierId as number,
        invoice_number: formData.invoice_number,
        order_number: formData.order_number,
        delivery_date: formData.delivery_date,
        invoice_date: formData.invoice_date,
        note: formData.note,
        discount_amount: discountAmount,
        shipping_cost: shippingCost,
        import_duty: importDuty,
        items,
      });

      // Reset form
      setFormData({
        supplier_id: null,
        new_supplier_name: "",
        invoice_number: "",
        order_number: "",
        delivery_date: new Date().toISOString().split("T")[0],
        invoice_date: "",
        note: "",
      });
      setItems([]);
      setDiscountAmount(0);
      setShippingCost(0);
      setImportDuty(0);
      setSuccess(true);
    } catch (err: unknown) {
      console.error("Submit error:", err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <InventoryIcon color="primary" />
        <Typography variant="h5" fontWeight="600">
          Receive Stock
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2 }}>
        <ReceiveFormHeader formData={formData} setFormData={setFormData} />
      </Paper>

      <Paper variant="outlined" sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2 }}>
        <ProductVariantPicker items={items} setItems={setItems} />
      </Paper>

      {items.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2 }}>
            <ReceivingTable items={items} setItems={setItems} />
          </Paper>

          <Paper variant="outlined" sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2 }}>
            <CostSummary
              items={items}
              discountAmount={discountAmount}
              setDiscountAmount={setDiscountAmount}
              shippingCost={shippingCost}
              setShippingCost={setShippingCost}
              importDuty={importDuty}
              setImportDuty={setImportDuty}
            />
          </Paper>
        </>
      )}

      {(error || success) && (
        <Box mb={3}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">Stock receipt submitted successfully!</Alert>}
        </Box>
      )}

      <Box
        sx={{
          position: isMobile ? 'fixed' : 'static',
          bottom: isMobile ? 0 : 'auto',
          left: 0,
          right: 0,
          p: isMobile ? 2 : 0,
          backgroundColor: isMobile ? 'background.paper' : 'transparent',
          boxShadow: isMobile ? theme.shadows[4] : 'none',
          zIndex: isMobile ? 1000 : 'auto',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            (!formData.supplier_id && !formData.new_supplier_name.trim()) ||
            !formData.order_number ||
            items.length === 0
          }
          size="large"
          fullWidth={isMobile}
          startIcon={loading ? <CircularProgress size={24} color="inherit" /> : null}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {loading ? 'Processing...' : 'Submit Stock Receipt'}
        </Button>
      </Box>
    </Container>
  );
};

export default StockReceivePage;